import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    EventEmitter,
    input,
    InputSignal,
    OnInit,
    Output
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';

import { Observable, of } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { AsyncPipe, NgIf } from '@angular/common';
import { isEqual } from 'lodash-es';
import { DroppanelStyle } from '../../../../elements/components/droppanel/entities/droppanel-style.entity';
import { ShFormTagListBaseComponent } from '../../../../form-controls/components/form-tag-list/base-simple/form-tag-list-base.component';
import {
    FormTagListActionItem,
    FormTagListActionItemLocation
} from '../../../../form-controls/components/form-tag-list/main/form-tag-list-action-item.class';
import {
    FormTagListStyle,
    FormTagListStyling,
    FormTagListTagColor
} from '../../../../form-controls/entities/form-tag-list-style.entity';
import { TagSearchFilterStyle } from '../../base/entities/tag-search-filter-style';
import { IFilterData } from '../../entities/filter-data.entity';
import { SearchMode } from '../../entities/search-mode.entity';
import { TagSearchFilter } from './filter-tag-search.class';

@Component({
    selector: 'sh-filter-tag-search',
    templateUrl: './filter-tag-search.component.html',
    styleUrls: ['./filter-tag-search.component.scss'],
    standalone: true,
    imports: [NgIf, ShFormTagListBaseComponent, ReactiveFormsModule, AsyncPipe],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ShFilterTagSearchComponent implements OnInit {
    public tagValuesControl: UntypedFormControl = new UntypedFormControl();

    filter: InputSignal<TagSearchFilter | undefined> = input();
    aiEnabled = input(false);
    fullTextEnabled = input(false);

    $singleTag = computed(() => this.calculateSingleTag(this.aiEnabled(), this.fullTextEnabled()));
    $tagStyling = computed(() => this.calculateTagStyling(this.aiEnabled()));
    $droppanelStyling = computed(() => this.calculateDroppanelStyling(this.filter()));
    $placeholder = computed(() => this.calculatePlaceHolderMsg(this.filter()));
    $autoCompleteFct = computed(() => this.calculateAutocompleteFct(this.filter(), this.aiEnabled()));
    $actionItem = computed(() => this.calculateActionItem(this.filter()));

    @Output() filterChanged: EventEmitter<IFilterData> = new EventEmitter<IFilterData>();

    constructor() {
        this.initEffects();
    }

    // #region Effects
    private initEffects(): void {
        let previousAiEnabled: boolean | undefined = undefined;
        let previousFullTextEnabled: boolean | undefined = undefined;
        let previousFilter: TagSearchFilter | undefined = undefined;

        effect(() => {
            const filter: TagSearchFilter | undefined = this.filter();
            const aiEnabled: boolean = this.aiEnabled();
            const fullTextEnabled: boolean = this.fullTextEnabled();

            const isEqualFilter = isEqual(previousFilter?.filterData, filter?.filterData);
            const isEqualAiEnabled = previousAiEnabled === aiEnabled;
            const isEqualFullTextEnabled = previousFullTextEnabled === fullTextEnabled;

            if (!filter || (isEqualFilter && isEqualAiEnabled && isEqualFullTextEnabled)) {
                // Ignore duplicate triggers
                return;
            }

            const updatedValues = this.limitValues(fullTextEnabled, filter?.values ?? []);

            previousAiEnabled = aiEnabled;
            previousFullTextEnabled = fullTextEnabled;
            previousFilter = filter;

            this.tagValuesControl.setValue(updatedValues);
        });
    }
    // #endregion

    public ngOnInit(): void {
        this.initFormListeners();
    }

    // #region Listener
    private initFormListeners(): void {
        this.tagValuesControl.valueChanges.pipe(debounceTime(1)).subscribe(() => {
            this.emitSearchFilter();
        });
    }
    // #endregion

    // #region Events
    private emitSearchFilter(): void {
        const tagValues = this.tagValuesControl.value;

        const originalTagFilter: TagSearchFilter | undefined = this.filter();

        if (!originalTagFilter) {
            return;
        }

        const updatedFilter: IFilterData = {
            ...originalTagFilter.filterData,
            field: this.getSearchField(),
            values: tagValues
        };

        const originalFilter: IFilterData = originalTagFilter.filterData;

        if (!isEqual(originalFilter, updatedFilter)) {
            this.filterChanged.emit(updatedFilter);
        }
    }

    private limitValues(fullTextEnabled: boolean, values: string[]): string[] {
        const limitedValues = fullTextEnabled ? values.slice(0, 1) : values;
        return limitedValues;
    }

    // #endregion

    // #region Utils
    private getSearchField(): string {
        if (this.fullTextEnabled()) {
            return SearchMode.FullText;
        } else if (this.aiEnabled()) {
            return SearchMode.Semantic;
        }

        return SearchMode.Global;
    }

    private calculateSingleTag(aiEnabled: boolean, fullTextEnabled: boolean): boolean {
        // Only allow single tags if AI or FullText are active
        return aiEnabled || fullTextEnabled;
    }

    private calculateTagColor(aiEnabled: boolean): FormTagListTagColor {
        // Set color to green if AI active
        return aiEnabled ? FormTagListTagColor.Green : FormTagListTagColor.Default;
    }

    private calculateTagStyling(aiEnabled: boolean): FormTagListStyling {
        return {
            color: this.calculateTagColor(aiEnabled),
            style: FormTagListStyle.Embedded,
            modifiers: []
        };
    }

    private calculateDroppanelStyling(filter: TagSearchFilter | undefined): DroppanelStyle | undefined {
        return filter?.droppanelStyle;
    }

    private calculateAutocompleteFct(
        filter: TagSearchFilter | undefined,
        aiEnabled: boolean
    ): (term: string) => Observable<string[]> {
        const emptyAutocomplete = (term: string) => of([] as string[]);

        if (!aiEnabled && filter) {
            return filter.autocompleteFct;
        }

        return emptyAutocomplete;
    }

    private calculatePlaceHolderMsg(filter: TagSearchFilter | undefined): string {
        return filter?.placeholderMsg ?? '';
    }

    private calculateActionItem(filter: TagSearchFilter | undefined): FormTagListActionItem {
        const location: FormTagListActionItemLocation =
            filter?.style === TagSearchFilterStyle.SearchActionButton
                ? FormTagListActionItemLocation.Joined
                : FormTagListActionItemLocation.None;
        const actionItem: FormTagListActionItem = new FormTagListActionItem(location, 'search');
        actionItem.eventListener = () => this.emitSearchFilter();

        return actionItem;
    }
    // #endregion
}
